import { useState, useEffect } from "react";
import { loginUser } from "../services/authService";
import apiClient, { _get } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import { useLocation, useNavigate } from "react-router-dom";
import { removeSecureItem, setSecureItem } from "../utils/LocalStorageManager";
import Swal from "sweetalert2";

export default function Login() {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token");
  const bizCode = query.get("biz_code");

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [showDashboardList, setShowDashboardList] = useState(false);
  const [loading, setLoading] = useState(false);

  // Handle guest login with token + biz_code
  useEffect(() => {
    const validateGuestLogin = async () => {
      if (!token || !bizCode) return;
      setLoading(true);
      try {
        const response = await apiClient.post(
          apiUrls.checkBusinessUser,
          { biz_code: bizCode, token: token },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response?.data?.success) {
          const business = response.data.data["business"];
          const user = response.data.data["user"];
          const newToken = response.data.data["token"];

          setSecureItem("token", newToken);
          setSecureItem("user", user);
          setSecureItem("binfo", [business]);

          window.location.reload();
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Validation failed", err);
        setLoading(false);
        navigate("/login", { replace: true });
      }
    };

    validateGuestLogin();
  }, [token, bizCode, navigate]);

  // Normal login flow
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await loginUser(email, password);
      const checkBusiness = await apiClient.get(apiUrls.getBusinesses);

      if (checkBusiness?.data?.data?.length == 0) {
        removeSecureItem("token");
        removeSecureItem("user");
        Swal.fire({
          icon: "error",
          title: "No Business Found",
          text: "Sorry you have no businesses associated with your account.",
        });
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else if (checkBusiness?.data?.data?.length == 1) {
        setSecureItem("binfo", checkBusiness?.data?.data || []);
        window.location.reload();
      } else {
        setBusinesses(checkBusiness.data.data);
        setShowDashboardList(true);
      }
    } catch {
      setError("Invalid credentials");
    }
  };

  return (
    <main className="d-flex w-100">
      <div className="container d-flex flex-column">
        <div className="row vh-100">
          <div className="col-sm-10 col-md-8 col-lg-6 col-xl-5 mx-auto d-table h-100">
            <div className="d-table-cell align-middle">
              {loading ? (
                <div className="text-center">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  >
                    <span className="visually-hidden">Logging in...</span>
                  </div>
                  <p className="mt-3">Logging you into your business...</p>
                </div>
              ) : (
                <>
                  <div className="text-center mt-4">
                    <h1 className="h1 text-primary">Sparloon Dashboard</h1>
                    {!showDashboardList ? (
                      <p className="lead text-dark">Please, sign in to continue</p>
                    ) : (
                      <p className="lead text-dark">
                        Select your business to continue
                      </p>
                    )}
                  </div>

                  <div className="card">
                    <div className="card-body">
                      {!showDashboardList ? (
                        <div className="m-sm-3">
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}

                          <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-control form-control-lg"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">Password</label>
                              <input
                                type="password"
                                className="form-control form-control-lg"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                              />
                            </div>
                            <div className="form-check align-items-center">
                              <input
                                id="customControlInline"
                                type="checkbox"
                                className="form-check-input"
                              />
                              <label
                                className="form-check-label text-small"
                                htmlFor="customControlInline"
                              >
                                Remember me
                              </label>
                            </div>
                            <div className="d-grid gap-2 mt-3">
                              <button
                                type="submit"
                                className="btn btn-lg btn-primary"
                              >
                                Sign in
                              </button>
                            </div>
                          </form>
                        </div>
                      ) : (
                        <>
                          {businesses.map((business: any, b: number) => (
                            <button
                              key={business.code || b}
                              className="btn btn-lg btn-outline-primary mb-3 w-100"
                              onClick={() => {
                                setSecureItem("binfo", [business]);
                                window.location.reload();
                              }}
                            >
                              {business.name || `Business ${b + 1}`}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
